import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Clock, MapPin, ChevronDown, Search, LayoutGrid, MessageCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { services } from '@/data/services';
import { buildWhatsAppUrl, WHATSAPP_DISPLAY } from '@/components/WhatsAppLink';

const hiddenFromMenu = ['musique-ambiance', 'animations-enfants-famille', 'animateur-micro', 'art-de-la-rue', 'art-de-la-scene', 'art-du-cirque', 'arts-visuels', 'sport-spectacle', 'lieux-reception', 'restauration-traiteur', 'image-souvenirs', 'beaute-bien-etre', 'transport-voyages', 'aventure-loisirs', 'feu-de-jour', 'mascottes', 'materiels', 'photobooth'];
const menuServices = services.filter(s => !hiddenFromMenu.includes(s.slug));
import SearchOverlay from '@/components/SearchOverlay';
import logo from '@/assets/logo-cygnature.png';
import logoBlanc from '@/assets/logo-cygnature-blanc.png';

const navLinks = [
  { label: 'Accueil', path: '/' },
  { label: 'À propos', path: '/a-propos' },
  { label: 'Services', path: '/services', hasDropdown: true },
  { label: 'Portfolio', path: '/portfolio' },
  { label: 'FAQ', path: '/faq' },
  { label: 'Blog', path: '/blog' },
  { label: 'Contact', path: '/contact' },
];

const SiteHeader = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isHome = location.pathname === '/';
  const isTransparent = isHome && !scrolled;

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setServicesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(prev => !prev);
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, []);

  useEffect(() => {
    setServicesOpen(false);
    setMobileServicesOpen(false);
  }, [location.pathname]);

  return (
    <>
      {/* Top bar */}
      <div className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isTransparent ? 'bg-transparent' : 'bg-petrol'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-11 text-[13px]">
          <div className="flex items-center gap-5">
            <a href="tel:+33671351550" className="flex items-center gap-1.5 text-petrol-foreground/60 hover:text-petrol-foreground transition-colors">
              <Phone size={11} className="text-petrol-foreground/50" />
              06 71 35 15 50
            </a>
            <a href="https://maps.app.goo.gl/SPqj82CAvgbDAqUU6" target="_blank" rel="noopener noreferrer" className="hidden sm:flex items-center gap-1.5 text-petrol-foreground/60 hover:text-petrol-foreground transition-colors">
              <MapPin size={11} className="text-petrol-foreground/50" />
              8 Rue du Champ de la Gaule, 10120 Saint-Pouange
            </a>
          </div>
          <span className="flex items-center gap-1.5 text-petrol-foreground/60">
            <Clock size={11} className="text-petrol-foreground/50" />
            Lun-Ven : 9h-19h · Sam : 10h-18h
          </span>
        </div>
      </div>

      <header className={`fixed top-11 left-0 right-0 z-50 transition-all duration-300 ${isTransparent ? 'bg-transparent border-b border-transparent' : 'bg-background/95 backdrop-blur-md border-b border-border'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" onClick={() => window.scrollTo(0, 0)} className="flex items-center gap-4">
            <img src={logo} alt="Cygnature Production" className="h-16 w-auto" width={64} height={64} />
            <div className="hidden sm:block">
              <span className={`text-xs font-heading leading-tight block transition-colors duration-300 ${isTransparent ? 'text-white' : 'text-foreground'}`}>Agence événementielle, mariage</span>
              <span className={`text-xs font-heading leading-tight block transition-colors duration-300 ${isTransparent ? 'text-white' : 'text-foreground'}`}>&amp; production d'artistes à Troyes</span>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden xl:flex items-center gap-1">
            {navLinks.map((link) => (
              link.hasDropdown ? (
                <div key={link.path} className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setServicesOpen(!servicesOpen)}
                    className={`px-3 py-2 rounded-full text-sm font-medium transition-colors inline-flex items-center gap-1 ${
                      location.pathname.startsWith('/services')
                        ? 'text-primary'
                        : isTransparent ? 'text-white hover:text-primary' : 'text-foreground hover:text-primary'
                    }`}
                  >
                    {link.label}
                    <ChevronDown size={14} className={`transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`} />
                  </button>

                  <AnimatePresence>
                    {servicesOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -5 }}
                        transition={{ duration: 0.15 }}
                        className="absolute top-full left-0 mt-2 w-64 rounded-xl border border-border bg-card shadow-lg overflow-hidden"
                      >
                        <Link
                          to="/services"
                          onClick={() => { setServicesOpen(false); window.scrollTo(0, 0); }}
                          className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground hover:bg-muted/50 transition-colors border-b border-border"
                        >
                          <LayoutGrid size={15} className="text-primary shrink-0" strokeWidth={1.5} />
                          Tous les services
                        </Link>
                        {menuServices.map((s) => (
                          <Link
                            key={s.slug}
                            to={`/services/${s.slug}`}
                            onClick={() => { setServicesOpen(false); window.scrollTo(0, 0); }}
                            className="flex items-center gap-3 px-4 py-3 text-sm text-muted-foreground hover:bg-muted/50 hover:text-foreground transition-colors"
                          >
                            <s.icon size={15} className="text-primary shrink-0" strokeWidth={1.5} />
                            {s.title}
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => window.scrollTo(0, 0)}
                  className={`px-3 py-2 rounded-full text-sm font-medium transition-colors ${
                    location.pathname === link.path
                      ? 'text-primary'
                      : isTransparent ? 'text-white hover:text-primary' : 'text-foreground hover:text-primary'
                  }`}
                >
                  {link.label}
                </Link>
              )
            ))}
            <button
              onClick={() => setSearchOpen(true)}
              className={`ml-1 p-2 rounded-full transition-colors hover:text-primary ${isTransparent ? 'text-white' : 'text-foreground'}`}
              aria-label="Rechercher"
            >
              <Search size={18} />
            </button>
            <Link
              to="/artistes"
              onClick={() => window.scrollTo(0, 0)}
              className={`ml-2 px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                isTransparent
                  ? 'border-white/30 text-white hover:bg-white/10'
                  : 'border-primary/30 text-primary hover:bg-primary/5'
              }`}
            >
              Je suis Artiste…
            </Link>
            <Link
              to="/contact"
              onClick={() => window.scrollTo(0, 0)}
              className="ml-2 px-5 py-2 rounded-full text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              Prendre rendez-vous
            </Link>
          </nav>

          {/* Mobile actions */}
          <div className="xl:hidden flex items-center gap-0">
            <button
              onClick={() => setSearchOpen(true)}
              className={`p-2 transition-colors hover:text-primary ${isTransparent ? 'text-white' : 'text-foreground'}`}
              aria-label="Rechercher"
            >
              <Search size={22} />
            </button>
            <a
              href="tel:+33671351550"
              className={`p-2 transition-colors hover:text-primary ${isTransparent ? 'text-white' : 'text-foreground'}`}
              aria-label="Appeler"
            >
              <Phone size={22} />
            </a>
            <a
              href={buildWhatsAppUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className={`p-2 transition-colors hover:text-primary ${isTransparent ? 'text-white' : 'text-foreground'}`}
              aria-label="Contacter sur WhatsApp"
            >
              <MessageCircle size={22} />
            </a>
            <button
              onClick={() => setMobileOpen(true)}
              className={`p-2 ${isTransparent ? 'text-white' : 'text-foreground'}`}
              aria-label="Menu"
            >
              <Menu size={24} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile fullscreen menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
            className="fixed inset-0 z-[999] bg-card flex flex-col"
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <Link to="/" onClick={() => { setMobileOpen(false); window.scrollTo(0, 0); }} className="flex items-center gap-3">
                <img src={logo} alt="Cygnature Production" className="h-16 w-auto" width={64} height={64} />
                <div>
                  <span className="text-xs font-heading leading-tight block text-muted-foreground">Agence événementielle, mariage</span>
                  <span className="text-xs font-heading leading-tight block text-muted-foreground">&amp; production d'artistes à Troyes</span>
                </div>
              </Link>
              <button onClick={() => setMobileOpen(false)} className="p-2 text-foreground" aria-label="Fermer">
                <X size={24} />
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto px-4 py-2 flex flex-col gap-0.5">
              {navLinks.map((link) => (
                link.hasDropdown ? (
                  <div key={link.path}>
                    <button
                      onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                      className={`w-full flex items-center justify-between py-2 px-4 rounded-lg text-base transition-colors ${
                        location.pathname.startsWith('/services')
                          ? 'bg-muted text-primary font-semibold'
                          : 'text-foreground/80 hover:bg-muted/50'
                      }`}
                    >
                      {link.label}
                      <ChevronDown size={16} className={`transition-transform duration-200 ${mobileServicesOpen ? 'rotate-180' : ''}`} />
                    </button>
                    <AnimatePresence>
                      {mobileServicesOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <Link
                            to="/services"
                            onClick={() => { setMobileOpen(false); window.scrollTo(0, 0); }}
                            className="flex items-center gap-2 py-1.5 px-8 text-sm text-muted-foreground hover:text-primary transition-colors"
                          >
                            <LayoutGrid size={13} className="text-primary shrink-0" strokeWidth={1.5} />
                            Tous les services
                          </Link>
                          {menuServices.map((s) => (
                            <Link
                              key={s.slug}
                              to={`/services/${s.slug}`}
                              onClick={() => { setMobileOpen(false); window.scrollTo(0, 0); }}
                              className="flex items-center gap-2 py-1.5 px-8 text-sm text-muted-foreground hover:text-primary transition-colors"
                            >
                              <s.icon size={13} className="text-primary shrink-0" strokeWidth={1.5} />
                              {s.title}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => { setMobileOpen(false); window.scrollTo(0, 0); }}
                    className={`py-2 px-4 rounded-lg text-base transition-colors ${
                      location.pathname === link.path
                        ? 'bg-muted text-primary font-semibold'
                        : 'text-foreground/80 hover:bg-muted/50'
                    }`}
                  >
                    {link.label}
                  </Link>
                )
              ))}
            </nav>

            <div className="border-t border-border px-6 py-4 flex flex-col gap-2">
              <Link
                to="/artistes"
                onClick={() => { setMobileOpen(false); window.scrollTo(0, 0); }}
                className="w-full py-2.5 rounded-full border border-primary/30 text-center text-sm font-medium text-primary hover:bg-primary/5 transition-colors"
              >
                Je suis Artiste…
              </Link>
              <a
                href="tel:+33671351550"
                className="w-full py-2.5 rounded-full border border-border text-center text-sm font-medium text-foreground hover:border-primary hover:text-primary transition-colors flex items-center justify-center gap-2"
              >
                <Phone size={14} />
                {WHATSAPP_DISPLAY}
              </a>
              <a
                href={buildWhatsAppUrl()}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMobileOpen(false)}
                className="w-full py-2.5 rounded-full border border-border text-center text-sm font-medium text-foreground hover:border-primary hover:text-primary transition-colors flex items-center justify-center gap-2"
              >
                <MessageCircle size={14} />
                WhatsApp
              </a>
              <Link
                to="/contact"
                onClick={() => { setMobileOpen(false); window.scrollTo(0, 0); }}
                className="w-full py-2.5 rounded-full bg-primary text-primary-foreground text-center text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                Prendre rendez-vous
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
};

export default SiteHeader;
